import { isValidISODate, isValidTimeFormat, removeWhitespacesInIban } from '@mng/mng-ts-helpers';
import { isValidBIC, isValidIBAN } from 'ibantools';
import { ModelItem, ModelOption } from '../model-item';
import { IModelContext, jaOption } from '../model.types';
import { IValidationResult, ValidationFunction } from './model-validation';
import { findObjectForPropertyInModelContext } from '../util/model-helper';

export class ValidationTypes {
  static YES_REQUIRED = 'yes_required';
  static REQUIRED = 'required';
  static MINLENGTH = 'minlength';
  static MINVALUE = 'minvalue';
  static MAXLENGTH = 'maxlength';
  static MAXVALUE = 'maxvalue';
  static IBAN = 'iban';
  static BIC = 'bic';
  static DATE = 'date';
  static TIME = 'time';
  static FZ_KONTO = 'fz-konto';
}

export function createValidationResult(id: string, data: any = null, message?: string): IValidationResult {
  return { id, data, message };
}

export const requiredValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  if (modelItem.options.hasRequiredFunction()) {
    if (!modelItem.options.isRequired(modelContext)) {
      return null;
    }
  }
  const valid = !isEmptyInputValue(modelContext.value);
  return valid ? null : createValidationResult(ValidationTypes.REQUIRED, valid);
};
export const requiredTrueValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const valid = modelContext.value;
  return valid ? null : createValidationResult(ValidationTypes.REQUIRED, valid, 'Wert muss angehackt sein!');
};
export const maxlengthValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const valid = !modelContext.value || modelContext.value.length <= modelItem.options.maxlength;
  return valid ? null : createValidationResult(ValidationTypes.MAXLENGTH, valid);
};

export const ibanValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const valid = !modelContext.value || isValidIBAN(removeWhitespacesInIban(modelContext.value));
  return valid ? null : createValidationResult(ValidationTypes.IBAN, valid);
};

export const bicValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const angebotData = findObjectForPropertyInModelContext(modelContext, 'sparte');
  const iban = angebotData?.zahlungsart?.iban;

  const bicAndIbanCountryMatch =
    iban?.length > 0 &&
    removeWhitespacesInIban(iban).toUpperCase().slice(0, 2) ===
      removeWhitespacesInIban(modelContext.value).toUpperCase().slice(4, 6);
  const isBicValid =
    !modelContext.value || (isValidBIC(removeWhitespacesInIban(modelContext.value)) && bicAndIbanCountryMatch);

  return isBicValid ? null : createValidationResult(ValidationTypes.BIC, isBicValid);
};

export const dateValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  if (modelContext.value) {
    return isValidISODate(modelContext.value) ? null : createValidationResult(ValidationTypes.DATE, false);
  } else {
    return null;
  }
};

export const timeValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const valid = isValidTimeFormat(modelContext.value);
  return valid ? null : createValidationResult(ValidationTypes.TIME, valid);
};

export const minlengthValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  if (!modelContext.value) {
    return null;
  } else {
    const valueToCheck =
      typeof modelContext.value === 'string' ? modelContext.value.trim() : modelContext.value.toString().trim();
    const valid = valueToCheck.length >= modelItem.options.minlength;
    return valid ? null : createValidationResult(ValidationTypes.MINLENGTH, valid);
  }
};

export const minvalueValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const valid =
    modelContext.value === null || modelContext.value === undefined || modelContext.value >= modelItem.options.minvalue;
  return valid ? null : createValidationResult(ValidationTypes.MINVALUE, valid);
};

export const maxvalueValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const valid =
    modelContext.value === null || modelContext.value === undefined || modelContext.value <= modelItem.options.maxvalue;
  return valid ? null : createValidationResult(ValidationTypes.MAXVALUE, valid);
};

export const requiredValueOptionsValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  if (!modelItem.options.valueOptions) {
    throw Error('keine valueOptions definiert');
  }

  let valid = !isEmptyInputValue(modelContext.value);

  if (valid) {
    valid = !!modelItem.options.valueOptions.options.find(
      (validValueOption: ModelOption) => validValueOption.value === modelContext.value
    );
  }

  return valid ? null : createValidationResult(ValidationTypes.REQUIRED, valid);
};

export const requiredJaValueOptionValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext = {}
): IValidationResult => {
  const valid = jaOption.value === modelContext.value;
  return valid ? null : createValidationResult(ValidationTypes.YES_REQUIRED, valid);
};

function isEmptyInputValue(value: any): boolean {
  // we don't check for string here so it also works with arrays
  return value == null || value.length === 0;
}
